import { PropsWithChildren } from 'react';
import {
    Checkbox as AriaCheckbox,
    CheckboxProps as AriaCheckboxProps,
} from 'react-aria-components';
import styled, { css, CSSProperties } from 'styled-components';
import { colors } from '../utils/colors';
import { Box } from './Box';
import { ErrorText } from './Texts';

export interface CheckboxProps {
    onChange: AriaCheckboxProps['onChange'];
    isDisabled?: AriaCheckboxProps['isDisabled'];
    isSelected?: AriaCheckboxProps['isSelected'];
    isRequired?: AriaCheckboxProps['isRequired'];
    name?: AriaCheckboxProps['name'];
    isError?: AriaCheckboxProps['isInvalid'];
    excludeFromTabOrder?: AriaCheckboxProps['excludeFromTabOrder'];
    isButton?: boolean;
    errorMessage?: string;
}

export const Checkbox = (props: PropsWithChildren<CheckboxProps>) => {
    return (
        <AriaCheckbox
            onChange={props.onChange}
            isDisabled={props.isDisabled}
            isInvalid={props.isError}
            isSelected={props.isSelected}
            isRequired={props.isRequired}
            name={props.name}
            excludeFromTabOrder={props.excludeFromTabOrder}
        >
            {({
                isSelected,
                isPressed,
                isFocusVisible,
                isDisabled,
                isInvalid,
            }) => {
                const showErrorMessage = props.errorMessage && isInvalid;
                return (
                    <StyledCheckbox
                        isButton={props.isButton}
                        data-selected={isSelected}
                        data-pressed={isPressed}
                        data-focus-visible={isFocusVisible}
                        data-disabled={isDisabled}
                        data-invalid={isInvalid}
                    >
                        <Box gap="12">
                            <Box direction="row" wrap="nowrap" gap="12">
                                <div className="checkbox">
                                    <svg viewBox="0 0 18 18" aria-hidden="true">
                                        <polyline points="2 9 8 14 16 4" />
                                    </svg>
                                </div>
                                {props.children}
                            </Box>
                            {showErrorMessage && (
                                <ErrorText variant="body" weight="regular">
                                    {props.errorMessage}
                                </ErrorText>
                            )}
                        </Box>
                    </StyledCheckbox>
                );
            }}
        </AriaCheckbox>
    );
};

export type CheckboxStyles = {
    unselected: {
        default: CSSProperties;
        svg: {
            stroke?: string;
        };
        active: CSSProperties & { svg?: { stroke: string } };
        hover: CSSProperties;
        focus: CSSProperties;
        disabled: CSSProperties;
        error: CSSProperties & {
            hover?: CSSProperties;
            focus?: CSSProperties;
            active?: CSSProperties & { svg?: { stroke: string } };
        };
    };
    selected: {
        default: CSSProperties;
        svg: {
            stroke?: string;
        };
        active: CSSProperties & { svg?: { stroke: string } };
        hover: CSSProperties & { svg?: { stroke: string } };
        focus: CSSProperties & { svg?: { stroke: string } };
        disabled: CSSProperties & { svg?: { stroke: string } };
        error: CSSProperties & {
            hover?: CSSProperties;
            svg: { stroke: string };
            focus?: CSSProperties;
        };
    };
};

export const defaultCheckboxStyle: CheckboxStyles = {
    unselected: {
        default: {
            outline: `0.125rem solid ${colors.black}`,
            background: colors.white,
            transition: 'all 200ms',
        },

        svg: {
            stroke: colors.transparent,
        },
        hover: {
            outline: `0.125rem solid ${colors.black}`,
            background: colors.blue100,
        },
        focus: {
            outline: `0.25rem solid ${colors.indigo400}`,
            border: `0.125rem solid ${colors.black}`,
            background: colors.white,
        },
        disabled: {
            outline: `0.125rem solid ${colors.greyscaleBorderDisabled}`,
        },
        error: {
            outline: `0.125rem solid ${colors.red700}`,
            background: colors.white,
            hover: {
                background: colors.red100,
                outline: `0.125rem solid ${colors.red600}`,
            },
            focus: {
                outline: `0.25rem solid ${colors.indigo400}`,
                border: `0.125rem solid ${colors.red700}`,
            },
            active: {
                outline: `0.125rem solid ${colors.red700}`,
                background: colors.blue600,
                svg: {
                    stroke: colors.transparent,
                },
            },
        },
        active: {
            outline: `0.125rem solid ${colors.black}`,
            background: colors.blue600,
            svg: {
                stroke: colors.transparent,
            },
        },
    },
    selected: {
        active: {
            outline: `0.125rem solid ${colors.blue100}`,
            background: colors.blue100,
            svg: {
                stroke: colors.black,
            },
        },
        default: {
            background: colors.blue700,
            transition: 'all 200ms',
            outline: `0.125rem solid ${colors.blue700}`,
        },
        svg: {
            stroke: colors.white,
        },
        hover: {
            background: colors.blue600,
        },
        focus: {
            outline: `0.25rem solid ${colors.indigo400}`,
            background: `${colors.blue700}`,
            svg: {
                stroke: colors.white,
            },
        },
        disabled: {
            background: `${colors.greyscaleBorderDisabled}`,
            svg: {
                stroke: colors.white,
            },
        },
        error: {
            background: `${colors.red700}`,
            hover: {
                background: `${colors.red600}`,
            },
            svg: {
                stroke: colors.white,
            },
        },
    },
};

const StyledCheckbox = styled.div<{
    'data-selected': boolean;
    isButton?: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 24px;
    forced-color-adjust: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    ${(props) => {
        const isSelected = !!props['data-selected'];
        const variantStyles =
            props.theme.checkbox[isSelected ? 'selected' : 'unselected'];

        return css`
            .checkbox {
                width: 24px;
                height: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                box-sizing: border-box;
                flex-shrink: 0; /* make sure its always stays the px it should */

                ${variantStyles.default};

                &:hover {
                    ${variantStyles.hover};
                }

                &:focus-visible {
                    ${variantStyles.focus};
                }
            }

            svg {
                width: 1rem;
                height: 1rem;
                fill: none;
                stroke: ${variantStyles.svg?.stroke};
                strokewidth: 0.125rem;
                stroke-dasharray: 20.125rem;
                stroke-dashoffset: 66;
                transition: stroke-dashoffset 200ms;
            }

            &[data-selected='true'] .checkbox {
                ${variantStyles.default};

                svg {
                    stroke-dashoffset: 44;
                    stroke: ${variantStyles.svg?.stroke};
                }

                &:hover {
                    ${variantStyles.hover};
                }
            }

            &[data-pressed='true'] .checkbox {
                ${variantStyles.active};
                &:hover {
                    ${variantStyles.active};
                }
                svg {
                    stroke: ${variantStyles.active.svg?.stroke};
                }
            }

            /* Ensure both focus and invalid styles apply together */
            &[data-focus-visible='true'][data-invalid='true'] .checkbox {
                ${variantStyles.error.focus};
                svg {
                    stroke: ${variantStyles.error.svg?.stroke};
                }
            }

            &[data-focus-visible='true'] .checkbox {
                ${variantStyles.focus};
                svg {
                    stroke: ${variantStyles.focus.svg?.stroke};
                }
            }

            &[data-disabled='true'] .checkbox {
                ${variantStyles.disabled};
                svg {
                    stroke: ${variantStyles.disabled.svg?.stroke};
                }
                cursor: not-allowed;
            }

            &[data-invalid='true'] .checkbox,
            &[data-required='true'] .checkbox {
                ${variantStyles.error};
                svg {
                    stroke: ${variantStyles.error.svg?.stroke};
                }
                &:hover {
                    ${variantStyles.error.hover};
                }
                &:active {
                    ${variantStyles.error.active};
                    svg {
                        stroke: ${variantStyles.error.svg?.stroke};
                    }
                }
            }
        `;
    }}
`;
